import React, { useState, useEffect, useCallback } from 'react'
import { Box, Typography, IconButton, Icon, Divider } from '@mui/material'
import { format, addMonths, subMonths, differenceInDays } from 'date-fns'
import AdxFormModal from 'components/reusable/modal/AdxFormModal'
import CalendarBlock from 'components/reusable/AdxDateRangePicker/calendarBlock'
import AdxButton from 'components/custom/AdxButton'

const UpdateCalendarModal = ({
  calendarProps,
  openModal,
  setOpenModal,
  currentMonth,
  nextMonth,
  calendarValue,
  setCalendarValue,
  selectedFirst,
  setSelectedFirst,
  selectedSecond,
  setSelectedSecond,
  selectedDuration,
  setSelectedDuration,
  initialValue,
  setInitialValue,
  setDateChanged,
}) => {
  const [clickState, setClickState] = useState('FIRST')
  const [selectedDisabledCounter, setSelectedDisabledCounter] = useState(0)

  const handleClose = (event, reason) => {
    if (reason && reason === 'backdropClick') {
      return
    }
    setInitialValue({
      startDate: selectedFirst,
      endDate: selectedSecond,
      duration: selectedDuration,
    })
    setOpenModal(false)
    calendarProps.setValue({
      startDate: format(selectedFirst, 'dd/MM/yyyy'),
      endDate: format(selectedSecond, 'dd/MM/yyyy'),
      duration: selectedDuration,
    })
    if (clickState === 'SECOND') {
      setClickState('FIRST')
      // setSelectedDisabledCounter(0);
    }
  }

  const handleClick = (day) => {
    if (clickState === 'FIRST') {
      setClickState('SECOND')
      setSelectedFirst(day.object)
      setSelectedSecond('')
      setSelectedDuration(differenceInDays(day.object, day.object) + 1)
      setSelectedDisabledCounter(0)
    }
    if (clickState === 'SECOND') {
      setClickState('FIRST')
      if (differenceInDays(day.object, selectedFirst) < 1) {
        setSelectedFirst(day.object)
        setSelectedSecond(selectedFirst)
      } else {
        setSelectedSecond(day.object)
      }
    }
  }

  const handleSelectDuration = useCallback(() => {
    if (clickState === 'FIRST' && selectedFirst && selectedSecond) {
      setSelectedDuration(
        differenceInDays(selectedSecond, selectedFirst) + 1 - selectedDisabledCounter
      )
    }
  }, [clickState, selectedDisabledCounter]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    handleSelectDuration()
  }, [handleSelectDuration])

  const CalendarHeader = () => {
    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 5,
          py: 3,
        }}
      >
        <Box
          sx={{
            backgroundColor: 'default-background.main',
            flex: 0.9,
            display: 'flex',
            alignItems: 'center',
            gap: 3.5,
            p: 1.25,
            borderRadius: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
            <Typography variant="body1" color="light-gray-text.main">
              Start:
            </Typography>
            <Typography
              variant="body1"
              color={selectedFirst ? 'dark-text.main' : 'light-gray-text-2.main'}
            >
              {selectedFirst !== '' ? format(selectedFirst, `eee, dd/MM/yyyy`) : 'DD/MM/YYYY'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
            <Typography variant="body1" color="light-gray-text.main">
              End:
            </Typography>
            <Typography
              variant="body1"
              color={selectedSecond ? 'dark-text.main' : 'light-gray-text-2.main'}
            >
              {selectedSecond !== '' ? format(selectedSecond, `eee, dd/MM/yyyy`) : 'DD/MM/YYYY'}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
          }}
        >
          <AdxButton
            variant={'ghost'}
            onClick={() => setCalendarValue(new Date())}
            size="small"
            sx={{
              mx: 1,
              color: 'dark-text.main',
              textDecoration: 'underline',
              fontWeight: 500,
            }}
          >
            Today
          </AdxButton>
          <IconButton
            onClick={() => setCalendarValue(subMonths(calendarValue, 1))}
            sx={{
              border: '1px solid',
              borderColor: 'light-gray-text.main',
              borderRadius: 0.875,
              p: 0.25,
            }}
          >
            <Icon sx={{ color: 'dark-text.main' }}>chevron_left</Icon>
          </IconButton>
          <IconButton
            onClick={() => setCalendarValue(addMonths(calendarValue, 1))}
            sx={{
              border: '1px solid',
              borderColor: 'light-gray-text.main',
              borderRadius: 0.875,
              p: 0.25,
            }}
          >
            <Icon sx={{ color: 'dark-text.main' }}>chevron_right</Icon>
          </IconButton>
        </Box>
      </Box>
    )
  }

  return (
    <AdxFormModal
      open={openModal}
      onClose={handleClose}
      modalWidth="750px"
      customHeader={<CalendarHeader />}
      sxModal={{
        outline: 'none',
        overflow: 'auto',
        minHeight: '580px',
      }}
      headerBorder={false}
      childPadding={false}
    >
      <Box
        width={1}
        display="flex"
        flexDirection={{ xs: 'column', lg: 'row' }}
        justifyContent="space-between"
        minHeight={{ lg: '380px' }}
        columnGap={{ xs: 0, lg: 5 }}
        rowGap={{ xs: 5, lg: 0 }}
        px={5}
        py={1}
      >
        <Box
          sx={{
            width: { xs: 1, lg: 1 / 2 },
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <CalendarBlock
            calendarProps={calendarProps}
            calendar={currentMonth}
            value={calendarValue}
            clickState={clickState}
            handleClick={handleClick}
            selectedFirst={selectedFirst}
            selectedSecond={selectedSecond}
            setSelectedDisabledCounter={setSelectedDisabledCounter}
          />
        </Box>
        <Box
          sx={{
            width: { xs: 1, lg: 1 / 2 },
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <CalendarBlock
            calendarProps={calendarProps}
            calendar={nextMonth}
            value={addMonths(calendarValue, 1)}
            clickState={clickState}
            handleClick={handleClick}
            selectedFirst={selectedFirst}
            selectedSecond={selectedSecond}
            setSelectedDisabledCounter={setSelectedDisabledCounter}
          />
        </Box>
      </Box>
      <Divider />
      <Box
        sx={{
          px: 5,
          py: 4,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
      >
        <Box display="flex" alignItems="center" gap={1.875}>
          <Typography variant="body1" color="light-gray-text.main">
            Duration:
          </Typography>
          <Typography variant="body1" color="alternate.main">
            {`${selectedDuration} day(s)`}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1.875}>
          <AdxButton
            variant={'ghost'}
            disabled={clickState === 'SECOND'}
            onClick={() => {
              setSelectedFirst(initialValue.startDate)
              setSelectedSecond(initialValue.endDate)
              setSelectedDuration(initialValue.duration)
              setOpenModal(false)
              setDateChanged(false)
            }}
          >
            Cancel
          </AdxButton>
          <AdxButton
            variant={'contained'}
            onClick={handleClose}
            disabled={!selectedFirst || !selectedSecond || clickState === 'SECOND'}
            sx={{ width: '200px' }}
          >
            Select
          </AdxButton>
        </Box>
      </Box>
    </AdxFormModal>
  )
}

export default UpdateCalendarModal
